import React , { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { Box, Button, Divider, IconButton } from "@mui/material";
import { Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { QueryInfo } from "system/query/QueryInfo";
import { Query, queryOps, socialDatasetId } from "system/query/Query";
import TemplateSearchOptions from "./TemplateSearchOptions";
import QuickSearch from "./QuickSearch";
import { TemplateSearchDateRange } from "./TemplateSearchDateRange";
import SavedQueryDialog from "components/queries/savedQueries/SavedQueryDialog";
import { APIErrorAlert } from "components/apiHandling/APIErrorAlert";
import APIRequest, { APIError } from "system/APIRequest";
import { QueryLeafNode } from "system/query/QueryLeafNode";
import { optimizeQueries } from "system/query/QueryUtility";
import { currentUser, UserPreference, InfegyUser, permissionTest } from "system/InfegyUser";
import { DeleteForever } from "@mui/icons-material";
import i18n from "translation";
import dayjs from "dayjs";

export class TemplateQueryProps {
  // These two fields are used for quick search queries
  queryTitle: string = "";
  quickSearchContent: string = "";

  usingAIQueryBuilder: boolean = false;

  aiQueryBuilderCompletedPrompt?: string = undefined;
  aiQueryBuilderQuery?: Query = undefined;

  searchFieldIds: string[] = [];

  // Builder / saved queries will use this field instead
  savedQueryId?: string;

  selectedDatasetId?: string = undefined;
  queryInfo?: QueryInfo = undefined;

  constructor(user?: InfegyUser) {
    if (user) {
      const preference = user.getPreference(UserPreference.DEFAULT_QUICKSEARCH_VS_AI);
      this.usingAIQueryBuilder = preference !== "on" && permissionTest("ai_features");
    } else if (!permissionTest("ai_features")) {
      this.usingAIQueryBuilder = false;
    } else {
      this.usingAIQueryBuilder = true;
    }
  }

  // Helper method to create a proper copy that preserves class methods
  copy(): TemplateQueryProps {
    const copy = Object.assign(Object.create(Object.getPrototypeOf(this)), this);
    return copy;
  }
}

export interface TemplateSearchContentProps {
  fieldIds: string[];
  setFieldIds: (fieldIds: string[]) => void;

  showDashboardSelector?: boolean;
  showCloseButton?: boolean;

  dashboard: DashboardDocument | undefined;
  setDashboard: (dashboard: DashboardDocument | undefined) => void;

  templateQueries: TemplateQueryProps[];
  setTemplateQueries: (templateQueries: TemplateQueryProps[]) => void;

  useDefaults: boolean;
  setUseDefaults: (useDefaults: boolean) => void;

  globalStartDate: string | undefined;
  setGlobalStartDate: (date: string | undefined) => void;

  globalEndDate: string | undefined;
  setGlobalEndDate: (date: string | undefined) => void;

  onClose?: () => void;
}

export const maxTemplateQueries = 10;

export function buildQuickSearchQuery(queryTitle: string, queryContent: string, fieldIds: string[], datasetId: string): Query | undefined {
  if (!queryContent || !datasetId || !fieldIds?.length) {
    return;
  }

  // If no title is provided, use the query content to generate a title
  if (!queryTitle) {
    // Remove non-alphanumeric characters and extra spaces (Unicode inclusive)
    queryTitle = queryContent.replace(/[^\p{L}\p{N}]+/gu, " ").replace(/\s+/g, " ").trim().slice(0, 50);
    if (!queryTitle || !queryTitle.length) {
      queryTitle = i18n.t('query-form.create-query');
    }
  }

  // Build the query object from the provided parameters
  const query = new Query({
    dataset_id: datasetId,
    name: queryTitle,
    query: {
      op: "and",
      values: [{
        op: "boolean",
        fields: fieldIds,
        value: (queryContent).trim()
      }]
    }
  });

  return query;
}

// Save the current widget settings to local storage. See the note above for why this is done this way.
export function saveLastTemplateSearchSettings(dashboardId: string, fieldIds: string[], templateQueries: TemplateQueryProps[], globalStartDate?: string, globalEndDate?: string) : void {
  const lastTemplateSearchSettings = {
    dashboardId,
    fieldIds,
    templateQueries,
    globalStartDate,
    globalEndDate
  };
  localStorage.setItem("lastTemplateSearchSettings", JSON.stringify(lastTemplateSearchSettings));
}

export const submitTemplateQueries = async (options: {
    datasetId: string,
    dashboardId: string,
    fieldIds: string[],

    templateQueries: TemplateQueryProps[],

    globalStartDate?: string,
    globalEndDate?: string,

    setQueryInfoError: Function,
    setIsLoading: Function,

    navigate: Function,
    user?: InfegyUser
  }) => {
  const { datasetId, dashboardId, fieldIds, templateQueries, setQueryInfoError, navigate, globalStartDate, globalEndDate, setIsLoading, user } = options;

  if (!datasetId || !dashboardId) {
    return;
  }

  let globalQuery = undefined;
  if (globalStartDate) {
    globalQuery = new Query({
      dataset_id: datasetId,
      is_ephemeral: true,
      name: "Global Date Range"
    });
    if (globalEndDate) {
      globalQuery.query = new QueryLeafNode({ op: queryOps.range, field: "published", upper: globalEndDate, lower: globalStartDate });
    } else {
      globalQuery.query = new QueryLeafNode({ op: queryOps.greaterThanOrEqual, field: "published", value: globalStartDate });
    }
  }

  // save template queries and the selected dashboard and fields to the widget data
  try {
    const validTemplateQueries = templateQueries.filter((templateQuery) => {
      // filter out blank queries
      return !!templateQuery.quickSearchContent || templateQuery.savedQueryId;
    });
    const quickSearchQueries = validTemplateQueries.filter((templateQuery) => {
      // filter out saved queries so we only have queries that need to be saved
      return !!templateQuery.quickSearchContent && !templateQuery.savedQueryId && !templateQuery.usingAIQueryBuilder;
    }).map((templateQuery) => {
      let qsq = buildQuickSearchQuery(templateQuery.queryTitle, templateQuery.quickSearchContent, templateQuery.searchFieldIds, datasetId);
      return qsq;
    }).filter((query) => { return !!query; }) as Query[];

    // Optimize queries will throw an error if the queries are invalid,
    // So we should run it even if the boolean queries should not be converted.
    const optimizedQueries = await optimizeQueries(quickSearchQueries, user?.getPreference(UserPreference.SKIP_CONVERTING_QUICKSEARCH_BOOLEAN) === "on");

    const mergedOptimizedQueries = optimizedQueries.map((optimizedQuery, index) => {
      const templateQuery = quickSearchQueries[index];

      return {
        ...templateQuery,
        ...optimizedQuery
      };
    });

    const aiPromptEntries = validTemplateQueries.filter((templateQuery) => {
      // filter out saved queries so we only have queries that need to be saved
      const content = templateQuery?.quickSearchContent?.trim() || "";
      return content.length && !!templateQuery.quickSearchContent && !templateQuery.savedQueryId && templateQuery.usingAIQueryBuilder;
    });

    const aiGenPromises = aiPromptEntries.map((templateQuery) => {
        return APIRequest(`query/ai-assist`, {
          prompt: templateQuery.quickSearchContent
        });
    });

    const gennedAIQueries = await Promise.all(aiGenPromises);

    const aiQueries = gennedAIQueries.map((aiQuery, index) => {
      const content = aiPromptEntries[index].quickSearchContent.trim();
      return new Query({
        dataset_id: datasetId,
        query: aiQuery,
        prompt: content,
        name: content.slice(0, 150)
      });
    });

    // Create requests for each quick search query
    const queryAPIPromises = mergedOptimizedQueries.concat(aiQueries).map((quickSearchQuery) => {
      return APIRequest("obj/query", {}, {
        method: "POST",
        "body": JSON.stringify(quickSearchQuery, undefined , 2)
      });
    });

    // Wait for all the API requests to finish
    const queryAPIResponses = await Promise.all(queryAPIPromises);

    // Check if any of the API requests failed
    const anyFailed = queryAPIResponses.some(response => !response.id);

    let globalQueryId = undefined;
    if (globalQuery) {
      const globalQueryResponse = await APIRequest("obj/query", {}, {
        method: "POST",
        "body": JSON.stringify(globalQuery, undefined , 2)
      });
      globalQueryId = globalQueryResponse.id;
    }

    // If none of the API requests failed, navigate to the dashboard
    if (!anyFailed) {
      // Build new TemplateQueries from the response and existing saved queries
      let lastResponseIndex = 0;
      const savedTemplateQueries:TemplateQueryProps[] = validTemplateQueries.map((templateQuery) => {
        if (!templateQuery.savedQueryId) {
          const queryResponse = queryAPIResponses[lastResponseIndex++];
          return {
            queryTitle: queryResponse?.name,
            savedQueryId: queryResponse?.id
          } as TemplateQueryProps;
        }
        return {
          queryTitle: templateQuery.queryTitle,
          savedQueryId: templateQuery.savedQueryId
        } as TemplateQueryProps;
      });

      let pageSection = "";

      // extract the section of the URL that contains the dashboard id. Dashboard ids always start with "d_"
      const currentDashboardId = window.location.href.split('/').find((section) => section.startsWith("d_"));

      // If we are on a page already, and we are not navigating to a new dashboard, make sure we stay on the same page
      if (window.location.href.includes("/page/dp_") && currentDashboardId === dashboardId) {
        // extract page in this format at the end of the URL page/dp_dvNwInIw6D5
        const pageId = window.location.href.split('/').pop();
        pageSection = pageId ? `page/${pageId}` : "";
      }

      // Save the last template search settings to local storage
      saveLastTemplateSearchSettings(dashboardId, fieldIds, savedTemplateQueries, globalStartDate, globalEndDate);

      // Get the list of saved query ids and use them to navigate to the dashboard
      const navIds = savedTemplateQueries.map(templateQuery => templateQuery.savedQueryId);

      APIRequest(`dashboard-template-log`, {}, {
        method: "POST",
        body: JSON.stringify({
          dashboard_id: dashboardId,
          query_ids: navIds.filter((queryId) => !!queryId ) as string[],
          global_query_id: globalQueryId ? globalQueryId : undefined
        })
      }).catch((error) => {
        console.error(`Error logging template dashboard usage: ${error}`);
        Sentry.captureException(error);
      });

      if (globalQueryId) {
        navIds.push(`g_${globalQueryId}`);
      }

      setIsLoading(false);

      navigate(`/dashboard/${dashboardId}/${navIds.join("-")}/${pageSection}`);
      // check if the user is already on the dashboard page, in which case we need to force a refresh
      // To clear the cache and ensure updated queries are loaded
      if (currentDashboardId === dashboardId) {
        window.location.reload();
      }
    }
  } catch (error) {
    console.error(`Error submitting template queries: ${error}`);
    Sentry.captureException(error);
    setQueryInfoError(error as APIError);
  }
}

export function TemplateSearchContent(props: TemplateSearchContentProps): JSX.Element {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const user = currentUser();

  const {dashboard, setDashboard, templateQueries, setTemplateQueries, globalStartDate, setGlobalStartDate, globalEndDate, setGlobalEndDate} = props;

  const canSubmitQueries = user?.permissions?.includes("run_query");

  const [fieldIds, setFieldIds] = useState<string[]>([]);
  const [useDefaults, setUseDefaults] = useState<boolean>(true);
  const [queryInfo, setQueryInfo] = useState<QueryInfo | undefined>(undefined);
  const [queryInfoError, setQueryInfoError] = useState<APIError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [savedQueryDialogIsOpen, setSavedQueryDialogIsOpen] = useState<boolean>(false);

  const [hasGlobalDateEditor, setHasGlobalDateEditor] = useState<boolean>(props.globalStartDate !== undefined);

  // Enable the Global Date Editor if a global start date is set
  useEffect(() => {
    setHasGlobalDateEditor(props.globalStartDate !== undefined);
  }, [props.globalStartDate]);


  // Load the query info for the selected dashboard's dataset
  useEffect(() => {
    if (!dashboard?.dataset_id) {
      return;
    }
    // Load the query info for the selected dashboard's dataset
    APIRequest(`query-info/${dashboard.dataset_id}`, {}, {
      method: "GET"
    }).then((response: any) => {
      setQueryInfoError(undefined);
      setQueryInfo(new QueryInfo(response));
    }).catch((error: APIError) => {
      setQueryInfoError(error);
      console.error(`Error loading query info: ${error}`);
      Sentry.captureException(error);
      setQueryInfo(undefined);
    });
  }, [dashboard?.dataset_id]);

  const canSubmit = useMemo(() => {
    return dashboard && templateQueries.every((templateQuery) => {
      return (!!templateQuery.quickSearchContent && (templateQuery.usingAIQueryBuilder || templateQuery.searchFieldIds?.length)) || templateQuery.savedQueryId;
    });
  }, [dashboard, fieldIds, templateQueries]);

  const clearQueryForm = () => {
    // clear the local storage cache of the template queries
    setIsLoading(false);
    localStorage.removeItem("lastTemplateSearchSettings");
    setGlobalStartDate(undefined);
    setGlobalEndDate(undefined);
    setHasGlobalDateEditor(false);
    setTemplateQueries([new TemplateQueryProps(user)]);
  }

  useEffect(() => {
    // Ensure we always have at least one template query
    if (!templateQueries || templateQueries.length === 0) {
      setTemplateQueries([new TemplateQueryProps(user)]);
      setIsLoading(false);
    }
  }, []);

  const submitSearch = () => {
    if (dashboard?.dataset_id && dashboard?.id) {
      const canHaveGlobalDates = hasGlobalDateEditor && queryInfo?.dataset?.id === socialDatasetId;
      const localGlobalStartDate = canHaveGlobalDates ? globalStartDate : undefined;
      const localGlobalEndDate = canHaveGlobalDates ? globalEndDate : undefined;
      setIsLoading(true);

      submitTemplateQueries({
        datasetId: dashboard?.dataset_id,
        dashboardId: dashboard?.id,
        fieldIds: fieldIds,
        templateQueries: templateQueries,
        globalStartDate: localGlobalStartDate,
        globalEndDate: localGlobalEndDate,
        setQueryInfoError: setQueryInfoError,
        setIsLoading: setIsLoading,
        navigate: navigate,
        user: user
      }).then(() => {
        setIsLoading(false);
        props.onClose && props.onClose();
      }).catch((error) => {
        setQueryInfoError(error as APIError);
        setIsLoading(false);
        props.onClose && props.onClose();
      });
    }
  }

  const activeQueries = templateQueries.filter((templateQuery) => {
    return !!templateQuery.quickSearchContent || templateQuery.savedQueryId || !!templateQuery.aiQueryBuilderQuery;
  });
  const availableQuerySlots = maxTemplateQueries - activeQueries.length;

  const publishedField = queryInfo?.fields.find((field) => field.id === "published");
  const minDate = publishedField ? dayjs(publishedField.min) : undefined;

  return (
    <Box sx={{ maxHeight: "100%", display: "grid", gridTemplateRows: "auto 1fr auto" }}>
      {canSubmitQueries &&
      <>
      <TemplateSearchOptions
        showDashboardSelector={props.showDashboardSelector ?? true}
        selectedDashboardOption={dashboard}
        setSelectedDashboardOption={setDashboard}
        onAddNewQuery={() => {
          setTemplateQueries([...templateQueries, new TemplateQueryProps(user)]);
        }}
        onLoadQuery={() => {
          setSavedQueryDialogIsOpen(true);
        }}
        showAddButtons={templateQueries.length < maxTemplateQueries}
      ></TemplateSearchOptions>
      {dashboard?.dataset_id && queryInfo &&
        <Box sx={{ maxHeight: "100%", overflowY: "auto", m:0, p:0 }}>
          {templateQueries.map((templateQuery, index) => (
            <Box key={index} sx={{ m: 0, backgroundColor: "background.default" }}>
              {dashboard?.dataset_id && queryInfo &&
                <QuickSearch
                  key={index}
                  index={index}
                  queryInfo={queryInfo}
                  datasetId={dashboard?.dataset_id}
                  canDelete={templateQueries.length > 1}
                  onSubmit={submitSearch}
                  templateQuery={templateQuery}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  setTemplateQuery={(templateQuery:TemplateQueryProps) => {
                    setTemplateQueries(prevQueries => {
                      const newQueries = [...prevQueries];
                      newQueries[index] = templateQuery;
                      return newQueries;
                    });
                  }}
                  onDelete={() => {
                    if (templateQueries && templateQueries.length > 1) {
                      const newQueries = [...templateQueries];
                      newQueries.splice(index, 1);
                      setTemplateQueries(newQueries);
                    } else {
                      clearQueryForm();
                    }
                  }} />
              }
              <Divider />
            </Box>
          ))}
        {templateQueries.length < maxTemplateQueries &&
          <Box sx={{ m: 0, backgroundColor: "background.default" }}>
          </Box>
        }
        </Box>
      }
      {!dashboard?.dataset_id &&
        <Alert severity="warning" sx={{ m: 2 }}>
          {i18n.t('widget-types.template-search.no-dataset-selected')}
        </Alert>
      }

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 470px" }, gap: 1, m: 1 }}>
        {(queryInfo?.dataset?.id === socialDatasetId) && hasGlobalDateEditor &&
          <Box sx={{ textAlign: "left" }}>
            <TemplateSearchDateRange
              minDate={minDate}
              startDate={globalStartDate}
              endDate={globalEndDate}
              setStartDate={setGlobalStartDate}
              setEndDate={setGlobalEndDate}
            />
            <IconButton
              onClick={() => {
                setGlobalStartDate(undefined);
                setGlobalEndDate(undefined);
                setHasGlobalDateEditor(false);
              }}>
              <DeleteForever />
            </IconButton>
          </Box>
        }
        {(queryInfo?.dataset?.id === socialDatasetId) && !hasGlobalDateEditor &&
          <Box sx={{ textAlign: "left" }}>
            {(canSubmit || templateQueries.length > 1) &&
            <Button
              sx={{ m: 0, opacity: 0.7, "&:hover": { opacity: 1 } }}
              variant="text"
              onClick={() => {
                setHasGlobalDateEditor(true);
              }}>
              {i18n.t('query-form.add-global-date-range')}
            </Button>
            }
          </Box>
        }
        {(queryInfo?.dataset?.id !== socialDatasetId) &&
          <Box></Box>
        }
        <Box sx={{ textAlign: {xs: "left", md: "right" }}}>
          {(canSubmit || templateQueries.length > 1) &&
            <Button
              sx={{ m: 0, opacity: 0.5, "&:hover": { opacity: 1 } }}
              variant="text"
              color="error"
              onClick={() => {
                clearQueryForm();
              }}
            >
              {i18n.t('widget-types.template-search.clear-query-form')}
            </Button>
          }
          {props.showCloseButton &&
            <Button
              sx={{ m: 0, ml: 1, display:"inline-block" }}
              variant="contained"
              color="secondary"
              onClick={() => {
                props.onClose && props.onClose();
              }
            }>
              {i18n.t('close')}
            </Button>
          }
          <Button
            sx={{ m: 0, ml: 1, display:"inline-block" }}
            variant="contained"
            type="submit"
            disabled={!canSubmit}
            onClick={() => {
              submitSearch();
            }} >
            {i18n.t('widget-types.template-search.run')}
          </Button>
        </Box>
      </Box>
      </>
      }
      {!canSubmitQueries &&
        <Alert severity="warning" sx={{ m: 4 }}>
          {i18n.t("account-status.insufficient-account-permissions")}
        </Alert>
      }
      {queryInfoError &&
        <APIErrorAlert fetchError={queryInfoError}></APIErrorAlert>
      }

      {savedQueryDialogIsOpen &&
        <SavedQueryDialog
          isOpen={savedQueryDialogIsOpen}
          showSelectAllButton={true}
          allowMultipleSelection={true}
          maximumSelectedQueries={availableQuerySlots}
          onSubmitAll={(queries:Query[]) => {
            let localTemplateQueries = templateQueries;
            // add the new queries to the form, up to the maximum allowed
            queries.forEach((query) => {
              if (localTemplateQueries.length < maxTemplateQueries) {
                // look for a blank query and remove one if found
                const emptyQuery = localTemplateQueries.find((tq) => !tq.quickSearchContent && !tq.savedQueryId && !tq.aiQueryBuilderQuery);
                if (emptyQuery) {
                  localTemplateQueries = localTemplateQueries.filter((tq) => tq !== emptyQuery);
                }
                // add the query to the form
                localTemplateQueries = [...localTemplateQueries, { queryTitle: query?.name, quickSearchContent: "", savedQueryId: query.id }];
              }
            });
            setTemplateQueries(localTemplateQueries);
            setSavedQueryDialogIsOpen(false);
          }}
          onSubmit={(submittedQueries:Query[]) => {
            let localTemplateQueries = templateQueries;
            // look for a blank query and remove one if found
            const emptyQuery = localTemplateQueries.find((query) => !query.quickSearchContent && !query.savedQueryId && !query.aiQueryBuilderQuery);
            if (emptyQuery) {
              localTemplateQueries = localTemplateQueries.filter((query) => query !== emptyQuery);
            }
            const newTemplateQueries = submittedQueries.map((query) => {
              return {
                queryTitle: query.name,
                quickSearchContent: "",
                savedQueryId: query.id
              } as TemplateQueryProps;
            });

            // add the queries to the form
            setTemplateQueries([...localTemplateQueries, ...newTemplateQueries]);
            setSavedQueryDialogIsOpen(false);
          }}
          onClose={() => {
            setSavedQueryDialogIsOpen(false);
          }}
        />
      }

    </Box>
  );
}
